
export type MessageHandler = (payload: any) => void;

/**
 * WorkerBus: A message router that allows multiple components to share a single Web Worker.
 * Replaces direct worker.onmessage assignment with a subscription model.
 * 
 * Implements: Phase 1.1 of COHESIVE_PHASED_PLAN.md
 */
export class WorkerBus {
    private worker: Worker;
    private listeners: Map<string, Set<MessageHandler>> = new Map();
    // For general RPC (APP_CALL/APP_RESPONSE) handled centrally
    private rpcPending: Map<string, { resolve: (val: any) => void, reject: (err: any) => void, method?: string, startedAtMs?: number }> = new Map();
    private ready = false;
    private readyPromise: Promise<void>;
    private readyResolve: (() => void) | null = null;

    constructor(worker: Worker) {
        this.worker = worker;
        this.readyPromise = new Promise((resolve) => {
            this.readyResolve = resolve;
        });
        this.worker.addEventListener('message', this.handleMessage.bind(this));
    }

    private handleMessage(event: MessageEvent) {
        const { type, payload } = event.data;
        const response = typeof event.data?.response === 'string' ? event.data.response : '';

        // Desktop-only: Worker -> Host RPC (used to reach Tauri APIs from the worker).
        if (type === 'HOST_CALL') {
            const requestId = payload?.requestId;
            const cmd = String(payload?.cmd || '').trim();
            const args = payload?.args ?? {};
            const sentAtMsRaw = Number(payload?.sentAtMs ?? NaN);
            const sentAtMs = Number.isFinite(sentAtMsRaw) ? sentAtMsRaw : Date.now();
            const buildTransferList = (val: any): Transferable[] => {
                const out: Transferable[] = [];
                const seen = new Set<ArrayBuffer>();

                const isSharedArrayBuffer = (b: any): boolean => {
                    try {
                        return typeof SharedArrayBuffer !== 'undefined' && b instanceof SharedArrayBuffer;
                    } catch {
                        return false;
                    }
                };

                const addArrayBuffer = (buf: any) => {
                    try {
                        if (!buf) return;
                        if (isSharedArrayBuffer(buf)) return;
                        if (!(buf instanceof ArrayBuffer)) return;
                        if (seen.has(buf)) return;
                        seen.add(buf);
                        out.push(buf);
                    } catch {
                        // ignore
                    }
                };

                const visit = (v: any, depth: number) => {
                    if (depth > 3) return;
                    if (!v) return;

                    try {
                        if (v instanceof ArrayBuffer) {
                            addArrayBuffer(v);
                            return;
                        }
                    } catch { }

                    try {
                        if (typeof ArrayBuffer !== 'undefined' && typeof ArrayBuffer.isView === 'function' && ArrayBuffer.isView(v)) {
                            // TypedArray/DataView
                            const buf = (v as any).buffer;
                            addArrayBuffer(buf);
                            return;
                        }
                    } catch { }

                    // Shallow recursive walk for common nested shapes (e.g. { bytes: Uint8Array }).
                    if (Array.isArray(v)) {
                        for (let i = 0; i < Math.min(32, v.length); i++) visit(v[i], depth + 1);
                        return;
                    }
                    if (typeof v === 'object') {
                        const keys = Object.keys(v);
                        for (let i = 0; i < Math.min(32, keys.length); i++) {
                            const k = keys[i];
                            try { visit((v as any)[k], depth + 1); } catch { }
                        }
                    }
                };

                visit(val, 0);
                return out;
            };
            const reply = (ok: boolean, result?: any, error?: any) => {
                try {
                    const msg = {
                        type: 'HOST_RESPONSE',
                        payload: { requestId, ok, result, error: error ? String(error?.message || error) : null }
                    };
                    const transfer = ok ? buildTransferList(result) : [];
                    if (transfer.length > 0) {
                        this.worker.postMessage(msg, transfer);
                    } else {
                        this.worker.postMessage(msg);
                    }
                } catch (e) {
                    console.warn('[WorkerBus] HOST_RESPONSE postMessage failed', e);
                }
            };

            const w: any = (globalThis as any);
            const isTauri = !!(w && (w.__FG_DESKTOP__ === true || w.__TAURI__ || w.__TAURI_INTERNALS__));
            const isElectron = !!(w && typeof w.__FG_ELECTRON_INVOKE__ === 'function');

            if (!isTauri && !isElectron) {
                reply(false, null, 'HOST_CALL received but not running in a native desktop shell (Tauri/Electron)');
                return;
            }

            // Dynamic import to avoid bundling Tauri in web builds.
            (async () => {
                try {
                    if (!cmd) throw new Error('HOST_CALL missing cmd');
                    if (isElectron) {
                        const invoke = w.__FG_ELECTRON_INVOKE__;
                        const result = await invoke(cmd, args);
                        if (result && typeof result === 'object' && !ArrayBuffer.isView(result)) {
                            const outAny = result as any;
                            const invokePerf = (outAny && typeof outAny.__fg_invoke_perf === 'object')
                                ? outAny.__fg_invoke_perf
                                : null;
                            outAny.__fg_host_hop_ms = Math.max(0, Date.now() - sentAtMs);
                            if (!invokePerf) {
                                outAny.__fg_invoke_perf = {
                                    bridge_total_ms: outAny.__fg_host_hop_ms,
                                };
                            }
                        }
                        reply(true, result, null);
                        return;
                    }
                    const mod: any = await import('@tauri-apps/api/core');
                    const invoke: any = mod?.invoke;
                    if (typeof invoke !== 'function') throw new Error('Tauri invoke unavailable');
                    const result = await invoke(cmd, args);
                    reply(true, result, null);
                } catch (e: any) {
                    reply(false, null, e);
                }
            })();
            return;
        }

        if (type === 'READY') {
            this.ready = true;
            this.readyResolve?.();
            this.readyResolve = null;
        }

        // 1. Handle APP_RESPONSE (RPC)
        // worker.ts sends: { type: 'APP_RESPONSE', requestId, payload: result, error: msg }
        if (type === 'APP_RESPONSE') {
            const requestId = event.data.requestId;
            const error = event.data.error;
            // payload is already extracted from event.data above
            const responseReceivedEpochMs = Date.now();
            const responseHandleStartedPerfMs = (typeof performance !== 'undefined' && typeof performance.now === 'function')
                ? performance.now()
                : Date.now();

            if (requestId && this.rpcPending.has(requestId)) {
                const pending = this.rpcPending.get(requestId)!;
                const { resolve, reject } = pending;
                this.rpcPending.delete(requestId);
                try {
                    const nowMs = (typeof performance !== 'undefined' && typeof performance.now === 'function')
                        ? performance.now()
                        : Date.now();
                    const elapsedMs =
                        (typeof pending?.startedAtMs === 'number' && Number.isFinite(pending.startedAtMs))
                            ? Math.max(0, nowMs - Number(pending.startedAtMs))
                            : null;
                    const payloadRows =
                        Number((payload as any)?.rows ?? (payload as any)?.rowCount ?? (Array.isArray((payload as any)?.data) ? (payload as any).data.length : (Array.isArray((payload as any)?.preview_rows) ? (payload as any).preview_rows.length : 0))) || 0;
                    if ((elapsedMs !== null && elapsedMs >= 20) || payloadRows >= 1000) {
                    }
                } catch { }
                try {
                    if (payload && typeof payload === 'object') {
                        const maybeLocalPerf = (payload as any).local_perf;
                        if (maybeLocalPerf && typeof maybeLocalPerf === 'object') {
                            (maybeLocalPerf as any).app_response_received_epoch_ms = responseReceivedEpochMs;
                            (maybeLocalPerf as any).workerbus_handle_started_epoch_ms = responseReceivedEpochMs;
                            (maybeLocalPerf as any).workerbus_pending_count_on_receive = this.rpcPending.size + 1;
                            if (typeof pending?.method === 'string' && pending.method) {
                                (maybeLocalPerf as any).workerbus_pending_method = pending.method;
                            }
                            if (typeof pending?.startedAtMs === 'number' && Number.isFinite(pending.startedAtMs)) {
                                const nowMs = (typeof performance !== 'undefined' && typeof performance.now === 'function')
                                    ? performance.now()
                                    : Date.now();
                                (maybeLocalPerf as any).workerbus_request_elapsed_to_receive_ms = Math.max(
                                    0,
                                    nowMs - Number(pending.startedAtMs)
                                );
                            }
                        }
                    }
                } catch {
                    // best-effort perf diagnostics only
                }
                try {
                    if (payload && typeof payload === 'object') {
                        const maybeLocalPerf = (payload as any).local_perf;
                        if (maybeLocalPerf && typeof maybeLocalPerf === 'object') {
                            const nowPerf = (typeof performance !== 'undefined' && typeof performance.now === 'function')
                                ? performance.now()
                                : Date.now();
                            (maybeLocalPerf as any).workerbus_handle_duration_ms = Math.max(
                                0,
                                nowPerf - responseHandleStartedPerfMs
                            );
                            (maybeLocalPerf as any).workerbus_handle_finished_epoch_ms = Date.now();
                        }
                    }
                } catch {
                    // best-effort perf diagnostics only
                }
                if (error) {
                    reject(error);
                } else {
                    resolve(payload);
                }
                return; // RPC responses are consumed by the promisor
            }
        }

        // 2. Broadcast to subscribers.
        // Some worker messages carry requestId in payload and are handled by subscribers
        // that manage their own pending maps.
        const routedType = type || response;
        if (routedType && this.listeners.has(routedType)) {
            const handlers = this.listeners.get(routedType)!;
            const routedPayload = type ? payload : {
                ...(payload && typeof payload === 'object' ? payload : { payload }),
                protocolVersion: event.data?.protocolVersion,
                response,
                requestId: event.data?.requestId,
                workbookId: event.data?.workbookId,
                sheetId: event.data?.sheetId,
                workbookRevision: event.data?.workbookRevision,
            };
            handlers.forEach(handler => {
                try {
                    handler(routedPayload);
                } catch (e) {
                    console.error(`[WorkerBus] Error in handler for ${routedType}`, e);
                }
            });
        }
    }

    /**
     * Subscribe to a specific message type.
     * Returns a function to unsubscribe.
     */
    subscribe(type: string, handler: MessageHandler): () => void {
        if (!this.listeners.has(type)) {
            this.listeners.set(type, new Set());
        }
        this.listeners.get(type)!.add(handler);

        return () => {
            const handlers = this.listeners.get(type);
            if (handlers) {
                handlers.delete(handler);
                if (handlers.size === 0) {
                    this.listeners.delete(type);
                }
            }
        };
    }

    /**
     * Send a fire-and-forget message to the worker.
     */
    send(type: string, payload?: any) {
        this.worker.postMessage({ type, payload });
    }

    /**
     * Send a typed spreadsheet-engine command to the worker.
     */
    sendCommand(command: string, message: Record<string, any>) {
        this.worker.postMessage({ ...message, command });
    }

    /**
     * Send a fire-and-forget message after the worker is ready.
     * This is required for bootstrap-style writes that must not be dropped during INIT.
     */
    sendWhenReady(type: string, payload?: any) {
        const send = () => {
            this.worker.postMessage({ type, payload });
        };
        if (this.ready) {
            send();
            return;
        }
        this.readyPromise.then(send).catch(() => {
            // ignore ready failures for fire-and-forget callers
        });
    }

    /**
     * Perform an RPC call (APP_CALL -> APP_RESPONSE).
     * This is the standard RPC mechanism for new features (like query builder).
     */
    request(method: string, args: any = {}, opts?: { timeoutMs?: number }): Promise<any> {
        return new Promise((resolve, reject) => {
            const requestId = Math.random().toString(36).substring(7);
            let done = false;
            const timeoutMs = Math.max(1000, Number(opts?.timeoutMs ?? 30000));
            const startedAtMs = (typeof performance !== 'undefined' && typeof performance.now === 'function')
                ? performance.now()
                : Date.now();
            const sentAtEpochMs = Date.now();

            const timer = setTimeout(() => {
                if (done) return;
                done = true;
                this.rpcPending.delete(requestId);
                reject(new Error(`RPC timeout for ${method}`));
            }, timeoutMs);

            const finishResolve = (val: any) => {
                if (done) return;
                done = true;
                clearTimeout(timer);
                resolve(val);
            };

            const finishReject = (err: any) => {
                if (done) return;
                done = true;
                clearTimeout(timer);
                reject(err);
            };

            const send = () => {
                if (done) return;
                this.rpcPending.set(requestId, { resolve: finishResolve, reject: finishReject, method, startedAtMs });
                this.worker.postMessage({
                    type: 'APP_CALL',
                    payload: {
                        requestId,
                        method,
                        sentAtEpochMs,
                        args: Array.isArray(args) ? args : [args] // Legacy adjustment: app.apply expects array
                    }
                });
            };

            if (this.ready) {
                send();
            } else {
                // Prevent early RPC calls (e.g. list_tabs) before the worker finishes INIT and emits READY.
                this.readyPromise.then(send).catch(finishReject);
            }
        });
    }

    /**
     * Send a request and wait for a specific response message type.
     * Useful for interacting with legacy worker handlers.
     */
    requestCustom(reqType: string, resType: string, payload?: any): Promise<any> {
        return new Promise((resolve) => {
            const unsub = this.subscribe(resType, (data) => {
                unsub();
                resolve(data);
            });
            this.send(reqType, payload);
        });
    }
}
